import React, { useEffect, useState } from 'react';
import {
  getPrestadorCadastro,
  getPrestadorHistorico,
  patchPrestadorCadastro,
  type ChangeLogEntry,
  type PrestadorCadastroData,
} from '../api';
import { maskPhone } from '../../../shared/utils/validators';

const FIELD_LABELS: Record<string, string> = {
  phone: 'Telefone',
  email: 'E-mail',
};

const MeuCadastroPage: React.FC = () => {
  const [data, setData] = useState<PrestadorCadastroData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Historico state
  const [historico, setHistorico] = useState<ChangeLogEntry[]>([]);
  const [historicoTotal, setHistoricoTotal] = useState(0);
  const [historicoPage, setHistoricoPage] = useState(1);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const historicoLimit = 10;

  const loadData = () => {
    setLoading(true);
    getPrestadorCadastro()
      .then((d) => {
        setData(d);
        setEditPhone(d.phone ?? '');
        setEditEmail(d.email ?? '');
      })
      .catch(() => setError('Erro ao carregar dados do cadastro.'))
      .finally(() => setLoading(false));
  };

  const loadHistorico = (page: number) => {
    setHistoricoLoading(true);
    getPrestadorHistorico(page, historicoLimit)
      .then((h) => {
        setHistorico(h.data);
        setHistoricoTotal(h.total);
        setHistoricoPage(h.page);
      })
      .catch(() => {})
      .finally(() => setHistoricoLoading(false));
  };

  useEffect(() => {
    loadData();
    loadHistorico(1);
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const payload: { phone?: string; email?: string } = {};
      if (editPhone !== (data?.phone ?? '')) payload.phone = editPhone;
      if (editEmail !== (data?.email ?? '')) payload.email = editEmail;
      if (Object.keys(payload).length === 0) {
        setSaveSuccess(true);
        setSaving(false);
        return;
      }
      const updated = await patchPrestadorCadastro(payload);
      setData(updated);
      setEditPhone(updated.phone ?? '');
      setEditEmail(updated.email ?? '');
      setSaveSuccess(true);
      loadHistorico(1);
    } catch {
      setSaveError('Erro ao salvar. Verifique os dados e tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Carregando...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  const totalPages = Math.ceil(historicoTotal / historicoLimit);

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold">Meu Cadastro</h1>

      {data && (
        <div className="bg-white rounded-lg shadow p-4 space-y-2">
          <div>
            <span className="font-semibold">Nome:</span> {data.name}
          </div>
          <div>
            <span className="font-semibold">Perfil:</span> {data.role}
          </div>
          <div>
            <span className="font-semibold">Tipo de vínculo:</span>{' '}
            {data.employmentType ?? 'Não informado'}
          </div>
        </div>
      )}

      {/* Edit form */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Atualizar contato</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              type="tel"
              inputMode="tel"
              value={editPhone}
              onChange={(e) => setEditPhone(maskPhone(e.target.value))}
              className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
              maxLength={16}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-mail
            </label>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
            />
          </div>
          {saveError && <p className="text-red-600 text-sm">{saveError}</p>}
          {saveSuccess && (
            <p className="text-green-600 text-sm">Dados atualizados com sucesso.</p>
          )}
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </form>
      </div>

      {/* Historico table */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Histórico de alterações</h2>
        {historicoLoading ? (
          <p className="text-sm text-gray-500">Carregando...</p>
        ) : historico.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhuma alteração registrada.</p>
        ) : (
          <>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3">Campo</th>
                  <th className="text-left py-2 px-3">Valor anterior</th>
                  <th className="text-left py-2 px-3">Novo valor</th>
                  <th className="text-left py-2 px-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((entry) => (
                  <tr key={entry.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">{FIELD_LABELS[entry.field] ?? entry.field}</td>
                    <td className="py-2 px-3 text-gray-500">{entry.oldValue ?? '—'}</td>
                    <td className="py-2 px-3">{entry.newValue ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-500">
                      {new Date(entry.changedAt).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="mt-3 flex gap-2 items-center text-sm">
                <button
                  disabled={historicoPage <= 1}
                  onClick={() => loadHistorico(historicoPage - 1)}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  ‹
                </button>
                <span>
                  Página {historicoPage} de {totalPages}
                </span>
                <button
                  disabled={historicoPage >= totalPages}
                  onClick={() => loadHistorico(historicoPage + 1)}
                  className="px-2 py-1 border rounded disabled:opacity-40"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MeuCadastroPage;
